import { useCallback, useEffect, useState } from "react";
import {
  Plus, Search, KeyRound, Ban, PlayCircle, Wallet, Percent,
  ChevronDown, ChevronUp, Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, apiError, aud } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const cents = (c) => aud((c || 0) / 100);
const fieldCls = "bg-[#050B18] border-slate-700 focus-visible:ring-blue-400 font-mono text-sm";

export default function ResellersTab() {
  const [resellers, setResellers] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", discount: "" });
  const [credit, setCredit] = useState({ mode: "add", amount: "", reason: "" });
  const [newDiscount, setNewDiscount] = useState("");
  const [tempPw, setTempPw] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ from: "", to: "", reseller_id: "" });

  const load = useCallback(() => {
    api.get("/admin/resellers", { params: search ? { search } : {} }).then(({ data }) => setResellers(data)).catch(() => {});
  }, [search]);

  const loadStats = useCallback(() => {
    const params = {};
    if (filters.from) params.date_from = filters.from;
    if (filters.to) params.date_to = filters.to;
    if (filters.reseller_id) params.reseller_id = filters.reseller_id;
    api.get("/admin/resellers-stats", { params }).then(({ data }) => setStats(data)).catch(() => {});
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const openDetail = async (rid) => {
    if (openId === rid) { setOpenId(null); setDetail(null); return; }
    setOpenId(rid);
    setDetail(null);
    setTempPw(null);
    try {
      const { data } = await api.get(`/admin/resellers/${rid}/detail`);
      setDetail(data);
      setNewDiscount(String(data.reseller.discount_percent));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/resellers", {
        email: form.email.trim(), password: form.password,
        discount_percent: parseFloat(form.discount || "0"),
      });
      toast.success("Reseller created — share the temp password; they'll be asked to change it");
      setForm({ email: "", password: "", discount: "" });
      load(); loadStats();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const setStatus = async (rid, status) => {
    try {
      await api.put(`/admin/resellers/${rid}`, { status });
      toast.success(`Account ${status}`);
      load(); loadStats();
      if (openId === rid) {
        const { data } = await api.get(`/admin/resellers/${rid}/detail`);
        setDetail(data);
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveDiscount = async (rid) => {
    try {
      await api.put(`/admin/resellers/${rid}`, { discount_percent: parseFloat(newDiscount) });
      toast.success("Discount updated — applies to future purchases only");
      load();
      const { data } = await api.get(`/admin/resellers/${rid}/detail`);
      setDetail(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const adjustCredit = async (rid) => {
    setBusy(true);
    try {
      const payload = { mode: credit.mode, reason: credit.reason.trim() };
      if (credit.mode === "set") payload.set_balance = parseFloat(credit.amount);
      else payload.amount = parseFloat(credit.amount);
      const { data } = await api.post(`/admin/resellers/${rid}/credit`, payload);
      toast.success(`Balance now ${cents(data.balance_cents)}`);
      setCredit({ mode: "add", amount: "", reason: "" });
      load(); loadStats();
      const { data: det } = await api.get(`/admin/resellers/${rid}/detail`);
      setDetail(det);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resetPassword = async (rid) => {
    try {
      const { data } = await api.post(`/admin/resellers/${rid}/reset-password`);
      setTempPw(data.temporary_password);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="resellers-tab">
      {/* stats */}
      {stats && (
        <div className="mb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
            {[
              ["Active resellers", stats.active_resellers, "stat-active-resellers"],
              ["Reseller sales", aud(stats.total_sales), "stat-reseller-sales"],
              ["Reseller orders", stats.total_orders, "stat-reseller-orders"],
              ["Credit deposited", aud(stats.total_deposited), "stat-credit-deposited"],
              ["Outstanding credit", aud(stats.outstanding_credit), "stat-outstanding-credit"],
            ].map(([label, value, tid]) => (
              <div key={tid} className="p-4 bg-[#0F1F38] border border-blue-900/40 rounded-lg">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</div>
                <div className="font-mono text-lg font-bold text-white" data-testid={tid}>{value}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="stats-filter-from" className={`${fieldCls} w-40`} />
            <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="stats-filter-to" className={`${fieldCls} w-40`} />
            <select value={filters.reseller_id} onChange={(e) => setFilters({ ...filters, reseller_id: e.target.value })} data-testid="stats-filter-reseller"
              className="bg-[#050B18] border border-slate-700 rounded-md px-3 py-2 text-sm font-mono text-slate-300">
              <option value="">All resellers</option>
              {resellers.map((r) => <option key={r.id} value={r.id}>{r.email}</option>)}
            </select>
          </div>
          {stats.by_reseller.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="p-4 bg-[#0F1F38] border border-blue-900/40 rounded-lg">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Sales by reseller</div>
                {stats.by_reseller.map((r) => (
                  <div key={r.reseller_id} className="flex justify-between text-sm py-1" data-testid={`sales-by-${r.reseller_id}`}>
                    <span className="text-slate-300 font-mono text-xs">{r.email}</span>
                    <span className="font-mono text-xs text-white">{r.orders} orders · {aud(r.sales)}</span>
                  </div>
                ))}
              </div>
              <div className="p-4 bg-[#0F1F38] border border-blue-900/40 rounded-lg">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Most popular reseller products</div>
                {stats.top_products.map((p) => (
                  <div key={p.name} className="flex justify-between text-sm py-1" data-testid={`top-product-${p.name.replace(/\W+/g, "-")}`}>
                    <span className="text-slate-300 text-xs">{p.name}</span>
                    <span className="font-mono text-xs text-white">{p.units} units · {aud(p.sales)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* create */}
      <div className="p-5 bg-[#0F1F38] border border-blue-900/40 rounded-lg mb-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-4">
          Create a reseller — they'll be forced to set their own password on first login
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" type="email" data-testid="reseller-email-input" className={fieldCls} />
          <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password * (8+ chars)" data-testid="reseller-temp-password-input" className={fieldCls} />
          <Input value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} placeholder="Discount % (e.g. 25)" type="number" min="0" max="90" data-testid="reseller-discount-input" className={fieldCls} />
          <button onClick={create} disabled={busy || !form.email.trim() || form.password.length < 8}
            data-testid="reseller-create-button"
            className="rounded-lg inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-400 text-[#050B18] text-xs font-mono font-bold uppercase tracking-widest hover:bg-blue-300 disabled:opacity-40 transition-all">
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
      </div>

      {/* search */}
      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email…" data-testid="reseller-search-input" className={`${fieldCls} pl-9`} />
      </div>

      {/* list */}
      <div className="space-y-2">
        {resellers.map((r) => (
          <div key={r.id} className="bg-[#0F1F38] border border-blue-900/40 rounded-lg overflow-hidden" data-testid={`reseller-row-${r.id}`}>
            <button onClick={() => openDetail(r.id)} data-testid={`reseller-expand-${r.id}`}
              className="w-full flex items-center gap-4 p-4 text-left hover:bg-[#12244a]/40 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm text-slate-100 truncate">{r.email}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                  {r.discount_percent}% off · created {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  {r.last_login_at ? ` · last login ${new Date(r.last_login_at).toLocaleDateString()}` : " · never logged in"}
                </div>
              </div>
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
                r.status === "active" ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10"
                : r.status === "suspended" ? "text-amber-300 border-amber-400/30 bg-amber-400/10"
                : "text-rose-300 border-rose-400/30 bg-rose-400/10"}`} data-testid={`reseller-status-${r.id}`}>{r.status}</span>
              <span className="font-mono text-sm font-bold text-emerald-300" data-testid={`reseller-balance-${r.id}`}>{cents(r.balance_cents)}</span>
              {openId === r.id ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>

            {openId === r.id && (
              <div className="border-t border-blue-900/40 p-5" data-testid={`reseller-detail-${r.id}`}>
                {!detail ? (
                  <div className="text-slate-500 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
                ) : (
                  <div className="grid lg:grid-cols-2 gap-6">
                    <div className="space-y-5">
                      {/* discount */}
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2"><Percent className="w-3 h-3 inline mr-1" />Discount</div>
                        <div className="flex gap-2">
                          <Input type="number" min="0" max="90" value={newDiscount} onChange={(e) => setNewDiscount(e.target.value)} data-testid="discount-edit-input" className={`${fieldCls} w-28`} />
                          <button onClick={() => saveDiscount(r.id)} data-testid="discount-save-button"
                            className="px-4 rounded-lg bg-blue-400 text-[#050B18] text-xs font-mono font-bold uppercase tracking-widest hover:bg-blue-300 transition-all">Save</button>
                        </div>
                      </div>
                      {/* credit */}
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2"><Wallet className="w-3 h-3 inline mr-1" />Manual credit adjustment</div>
                        <div className="flex flex-wrap gap-2">
                          <select value={credit.mode} onChange={(e) => setCredit({ ...credit, mode: e.target.value })} data-testid="credit-mode-select"
                            className="bg-[#050B18] border border-slate-700 rounded-md px-3 py-2 text-sm font-mono text-slate-300">
                            <option value="add">Add credit</option>
                            <option value="remove">Remove credit</option>
                            <option value="set">Set balance</option>
                          </select>
                          <Input type="number" min="0" step="0.01" value={credit.amount}
                            onChange={(e) => setCredit({ ...credit, amount: e.target.value })}
                            placeholder={credit.mode === "set" ? "New balance AUD" : "Amount AUD"}
                            data-testid="credit-amount-input" className={`${fieldCls} w-36`} />
                          <Input value={credit.reason} onChange={(e) => setCredit({ ...credit, reason: e.target.value })}
                            placeholder="Reason (required)" data-testid="credit-reason-input" className={`${fieldCls} flex-1 min-w-[160px]`} />
                          <button onClick={() => adjustCredit(r.id)} disabled={busy || !credit.amount || !credit.reason.trim()}
                            data-testid="credit-apply-button"
                            className="px-4 py-2 rounded-lg bg-emerald-500 text-[#050B18] text-xs font-mono font-bold uppercase tracking-widest hover:bg-emerald-400 disabled:opacity-40 transition-all">Apply</button>
                        </div>
                      </div>
                      {/* account actions */}
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => resetPassword(r.id)} data-testid="reset-pw-button"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-600 text-slate-300 text-xs font-mono font-bold uppercase tracking-widest hover:border-blue-400 transition-all">
                          <KeyRound className="w-3.5 h-3.5" /> Reset password
                        </button>
                        {r.status === "active" ? (
                          <button onClick={() => setStatus(r.id, "suspended")} data-testid="suspend-button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-500/40 text-amber-300 text-xs font-mono font-bold uppercase tracking-widest hover:bg-amber-500/10 transition-all">
                            <Ban className="w-3.5 h-3.5" /> Suspend
                          </button>
                        ) : (
                          <button onClick={() => setStatus(r.id, "active")} data-testid="reactivate-button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-500/40 text-emerald-300 text-xs font-mono font-bold uppercase tracking-widest hover:bg-emerald-500/10 transition-all">
                            <PlayCircle className="w-3.5 h-3.5" /> Reactivate
                          </button>
                        )}
                        {r.status !== "disabled" && (
                          <button onClick={() => { if (window.confirm("Permanently disable this reseller? They will not be able to sign in.")) setStatus(r.id, "disabled"); }} data-testid="disable-button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-rose-500/40 text-rose-300 text-xs font-mono font-bold uppercase tracking-widest hover:bg-rose-500/10 transition-all">
                            <Ban className="w-3.5 h-3.5" /> Disable
                          </button>
                        )}
                      </div>
                      {tempPw && (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-sm" data-testid="temp-password-result">
                          New temporary password: <span className="font-mono font-bold text-amber-300 select-all">{tempPw}</span>
                          <span className="block text-xs text-slate-500 mt-1">Shown once — send it to the reseller. They'll be forced to change it.</span>
                        </div>
                      )}
                      {/* deposits */}
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Deposits</div>
                        <div className="space-y-1">
                          {detail.deposits.slice(0, 8).map((d) => (
                            <div key={d.id} className="flex justify-between text-xs font-mono text-slate-400 py-1 border-b border-slate-800">
                              <span>{new Date(d.created_at).toLocaleDateString()}</span>
                              <span className="text-white">{cents(d.amount_cents)}</span>
                              <span className={d.status === "completed" ? "text-emerald-300" : d.status === "pending" ? "text-amber-300" : "text-rose-300"}>{d.status}</span>
                            </div>
                          ))}
                          {detail.deposits.length === 0 && <div className="text-xs text-slate-600 font-mono">No deposits</div>}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-5">
                      {/* transactions */}
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Transactions</div>
                        <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                          {detail.transactions.slice(0, 30).map((t) => (
                            <div key={t.id} className="flex justify-between gap-2 text-xs font-mono py-1 border-b border-slate-800" data-testid={`admin-txn-${t.id}`}>
                              <span className="text-slate-500">{new Date(t.created_at).toLocaleDateString()}</span>
                              <span className="text-slate-400 flex-1 truncate">{t.type}{t.note ? ` — ${t.note}` : ""} <span className="text-slate-600">({t.created_by})</span></span>
                              <span className={t.amount_cents >= 0 ? "text-emerald-300" : "text-rose-300"}>{t.amount_cents >= 0 ? "+" : ""}{cents(t.amount_cents)}</span>
                            </div>
                          ))}
                          {detail.transactions.length === 0 && <div className="text-xs text-slate-600 font-mono">No transactions</div>}
                        </div>
                      </div>
                      {/* orders */}
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Orders ({detail.orders.length})</div>
                        <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                          {detail.orders.slice(0, 30).map((o) => (
                            <div key={o.id} className="flex justify-between gap-2 text-xs font-mono py-1 border-b border-slate-800">
                              <span className="text-slate-500">{new Date(o.created_at).toLocaleDateString()}</span>
                              <span className="text-slate-300 flex-1 truncate">{(o.items || []).map((i) => i.name).join(", ")}</span>
                              <span className="text-white">{aud(o.total)}</span>
                              <span className={o.payment_status === "paid" ? "text-emerald-300" : "text-amber-300"}>{o.payment_status}</span>
                            </div>
                          ))}
                          {detail.orders.length === 0 && <div className="text-xs text-slate-600 font-mono">No orders</div>}
                        </div>
                      </div>
                      {/* audit */}
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Audit log</div>
                        <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                          {detail.audit.map((a) => (
                            <div key={a.id} className="text-[11px] font-mono text-slate-500 py-0.5">
                              {new Date(a.ts).toLocaleString()} · <span className="text-slate-300">{a.admin}</span> · {a.action} {a.detail && <span className="text-slate-600">— {a.detail}</span>}
                            </div>
                          ))}
                          {detail.audit.length === 0 && <div className="text-xs text-slate-600 font-mono">No admin actions recorded</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {resellers.length === 0 && (
          <div className="text-center py-16 font-mono text-sm text-slate-500 uppercase tracking-[0.25em]">No resellers yet</div>
        )}
      </div>
    </div>
  );
}
